import { Container, Typography } from "@material-ui/core";
import React, {
  useEffect, useMemo, useRef, useState
} from "react";
import { useMutation, useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { EditRequest, ProfileModal, CardOrderSchedule } from "../../components";
import { getFirebaseRealTimeDatabase } from "../../services/firebaseRealTimeDatabase";
import { useStore } from "../../store";
import {
  KitchenOrder,
  KitchenOrderClient,
  KitchenOrders,
  Sector,
} from "../../types";
import useStyles from "./styles";
import {
  useFetchOrdersForSchedule,
  useGetEntity,
  useGetSectorsActive,
} from "../../api/orders/hooks";
import { deliverOrder, progressOrder } from "../../services";
import { getClientNameForOrder } from "../../utils/getClientNameForOrder";
import { replaceSpecialCharacters } from "../../utils/strings";
import { usePrinter } from "../../hooks/usePrinter";
import { Select } from "../../components/Select";
import { useEntity } from "../../hooks/useEntity";
import CustomInput from "../../components/CustomInput";
import AlphabetIndex from "./AlphabetIndex";
import { redirectToAdminLogin } from "../../utils/navigationUtils";
import { OrderListSchedule } from "../../components/OrderListSchedule";
import {
  getScopedSectors,
  shouldShowSectorOthers,
} from "../../utils/sectorScope";
import { useOrderCancellation } from "../../hooks/useOrderCancellation";
import { formatKitchenTicketItems } from "../../utils/formatKitchenTicketItems";

export const ScheduledOrder: React.FC = () => {
  const Styles = useStyles();

  const queryClient = useQueryClient();

  const [clientProfile, setClientProfile] = useState<KitchenOrderClient | null>(
    null,
  );
  const [entityId, setEntityId] = useState<string>("");
  const [sectorsList, setSectorsList] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<KitchenOrder | null>(null);
  const [sectorSelected, setSectorSelected] = useState("all");
  const [sectorId, setSectorId] = useState("all");
  const { showSectors } = useEntity();
  const [filterName, setFilterName] = useState<string>("");
  const [filterDate, setFilterDate] = useState<string>("");
  const [pendingOrders, setPendingOrders] = useState<KitchenOrders>([]);
  const [progressOrders, setProgressOrders] = useState<KitchenOrders>([]);

  const [openModal, setOpenModal] = useState<boolean>(true);

  const { userManager, navigation } = useStore();
  const {
    requestCancellation,
    confirmation: cancellationConfirmation,
    isLoading: isCanceling,
  } = useOrderCancellation();

  interface ScheduledOrderLocationState {
    order?: KitchenOrder;
    isSchedule?: boolean;
    isEdit?: boolean;
  }

  const location = useLocation();
  const state = location.state as ScheduledOrderLocationState | undefined;

  const {
    data: _orders,
    isLoading,
    refetch: reFetchOrders,
  } = useFetchOrdersForSchedule(entityId);

  const { data: sectors } = useGetSectorsActive(entityId, true);
  const scopedSectors = useMemo(
    () => getScopedSectors(sectors, userManager.segmentId),
    [sectors, userManager.segmentId],
  );
  const hasFixedSegment = !!userManager.segmentId;

  const { data: entity } = useGetEntity(entityId, true);

  const { mutateAsync: patchOrderMutator, isLoading: isloadingProgress } =
    useMutation(progressOrder);
  const { mutateAsync: deliveryOrderMutator, isLoading: isloadingDelivery } =
    useMutation(deliverOrder);
  const { handlePrint, printers } = usePrinter();

  const [availableLetters, setAvailableLetters] = useState<string[]>([]);
  const alphabetRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleOpenModal = (order: KitchenOrder) => {
    if (entity?.managerAuthorization) {
      redirectToAdminLogin(navigation, order, { isSchedule: true });
    } else {
      setSelectedOrder(order);

      setOpenModal(true);
    }
  };

  useEffect(() => {
    if (state?.order && state?.isSchedule && state?.isEdit) {
      setSelectedOrder(state.order);
      setOpenModal(true);
    }
  }, [state?.order, state?.isSchedule, state?.isEdit]);

  const handleCloseModal = () => {
    setOpenModal(false);

    setSelectedOrder(null);
  };

  const filterOrdersByName = (order: any) => {
    if (!filterName) return true;
    const formattedFilterName = filterName.toLowerCase().replace(/\s+/g, "");
    const clientName = getClientNameForOrder(order)
      .toLowerCase()
      .replace(/\s+/g, "");
    return clientName.includes(formattedFilterName);
  };

  const filterOrdersByDate = (order: any) => {
    if (!filterDate) return true;
    const scheduleDate = format(new Date(order.scheduleFor), "yyyy-MM-dd", {
      locale: ptBR,
    });

    return scheduleDate === filterDate;
  };

  const handleProgressOrder = async (id: string) => {
    await patchOrderMutator(id);
    await queryClient.invalidateQueries([
      "ordersScheduleOrder",
      userManager?.entity,
      true,
    ]);
  };

  const handleDeliverOrder = async (id: string) => {
    await deliveryOrderMutator(id);
    await queryClient.invalidateQueries([
      "ordersScheduleOrder",
      userManager?.entity,
    ]);
  };

  const handleCancelOrder = async (order: KitchenOrder) => {
    if (entity?.managerAuthorization) {
      navigation.navigateTo("/login-admin", {
        state: {
          order,
          isSchedule: true,
        },
      });
    } else {
      await requestCancellation(order);
    }
  };

  const filterOrdersBySector = (order: any) => {
    if (hasFixedSegment) {
      return order.items?.some(
        (item: any) => item.segment?.id === userManager.segmentId,
      );
    }
    if (sectorId === "all") return true;

    if (sectorId === "others") {
      return order.items?.some((item: any) => !item.segment);
    }

    return order.items?.some((item: any) => item.segment?.id === sectorId);
  };

  const loadOrders = async () => {
    if (_orders) {
      if (_orders.pendingOrders) {
        setPendingOrders(
          _orders.pendingOrders
            .filter((order: any) => filterOrdersBySector(order))
            .filter((order: any) => filterOrdersByName(order))
            .filter((order: any) => filterOrdersByDate(order))
            .sort((firstOrder: any, secondOrder: any) => {
              const dataA: any = new Date(
                secondOrder?.scheduleFor || 0,
              ).getTime();
              const dataB: any = new Date(
                firstOrder?.scheduleFor || 0,
              ).getTime();
              return dataB - dataA;
            }),
        );
      } else {
        setPendingOrders([]);
      }
      if (_orders.progressOrders) {
        setProgressOrders(
          _orders.progressOrders
            .filter((order: any) => filterOrdersBySector(order))
            .filter((order: any) => filterOrdersByName(order))
            .filter((order: any) => filterOrdersByDate(order))
            .sort((firstOrder: any, secondOrder: any) => {
              const dataA: any = new Date(
                secondOrder?.scheduleFor || 0,
              ).getTime();
              const dataB: any = new Date(
                firstOrder?.scheduleFor || 0,
              ).getTime();
              return dataB - dataA;
            }),
        );
      } else {
        setProgressOrders([]);
      }
    }
  };

  const formatList = () => {
    const listSectors = scopedSectors?.map((_sector: Sector) => ({
      label: _sector.name,
      value: _sector.id,
    }));

    const listSortedSectors: any[] = listSectors?.sort((a: any, b: any) =>
      a.label.localeCompare(b.label),);

    if (!hasFixedSegment) {
      listSortedSectors.unshift({ label: "Todos", value: "all" });
    }
    if (shouldShowSectorOthers(userManager.segmentId)) {
      listSortedSectors.push({ label: "Outros", value: "others" });
    }

    setSectorsList(listSortedSectors);
  };

  const filterOrders = (id: string) => {
    setSectorSelected(id);
    setSectorId(id);
    loadOrders();
  };

  const groupOrdersByInitial = (ordersName: KitchenOrder[]) =>
    ordersName.reduce((acc: Record<string, KitchenOrder[]>, order) => {
      const initial = getClientNameForOrder(order)[0].toUpperCase();
      if (!acc[initial]) {
        acc[initial] = [];
      }
      acc[initial].push(order);
      return acc;
    }, {});

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const handleSelectLetter = (letter: string) => {
    const ref = alphabetRefs.current[letter];
    if (ref) {
      ref.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    getFirebaseRealTimeDatabase(userManager.entity, async () => {
      await reFetchOrders();
    });
  }, []);

  useEffect(() => {
    (async () => {
      await loadOrders();
    })();
  }, [_orders, sectorId]);

  useEffect(() => {
    if (_orders) {
      const grouped = groupOrdersByInitial(_orders.pendingOrders || []);
      setAvailableLetters(Object.keys(grouped).sort());
    }
  }, [_orders]);

  useEffect(() => {
    if (userManager.entity) {
      setEntityId(userManager.entity);
    }
  }, [userManager]);

  useEffect(() => {
    if (scopedSectors?.length) {
      formatList();
    }
  }, [scopedSectors, hasFixedSegment, userManager.segmentId]);

  useEffect(() => {
    if (hasFixedSegment && userManager.segmentId) {
      setSectorSelected(userManager.segmentId);
      setSectorId(userManager.segmentId);
    }
  }, [hasFixedSegment, userManager.segmentId]);

  useEffect(() => {
    loadOrders();
  }, [filterName, filterDate]);

  const handlePrintOrder = async (orderId: string) => {
    const allOrders = [
      ...(_orders.progressOrders || []),
      ...(_orders.pendingOrders || []),
    ];

    const order = allOrders.find((item: any) => item.id === orderId);

    if (!order) {
      return;
    }

    const orderDetails = `
      Cliente: ${order.client.name}
      Turma: ${order.client.identityNumber}
      Telefone: ${order.client.phone}
      Data do Pedido: ${new Date(order.createdAt).toLocaleString("pt-BR")}
      Agendado Para: ${
  order.isSchedule
    ? new Date(order.scheduleFor).toLocaleString("pt-BR")
    : "N/A"
}
      --------------------
      ${formatKitchenTicketItems(order.items)}
    `;

    await handlePrint(replaceSpecialCharacters(orderDetails), order.id);
  };

  return (
    <Container maxWidth={false} className={Styles.container}>
      {cancellationConfirmation}
      <div className={Styles.table}>
        <div className={Styles.selectsWrapper}>
          {sectorsList?.length > 0 && (showSectors || hasFixedSegment) && (
            <div className={Styles.filterSector}>
              <Select
                label="Setor"
                options={sectorsList}
                value={sectorSelected}
                onChange={(val) => filterOrders(val)}
              />
            </div>
          )}

          <div className={Styles.filterWrapper}>
            <CustomInput
              label="Buscar por nome"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <CustomInput
              label="Data de agendamento"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </div>

        </div>
        <hr className={Styles.line} />

        {isLoading && (
          <div className={Styles.tableBody}>
            <div className={Styles.tableRow}>
              <Typography
                noWrap
                className={`${Styles.tableCell} ${Styles.tableColumn2}`}
              >
                Loading data...
              </Typography>
            </div>
          </div>
        )}
        {!isLoading && (
          <div className={Styles.tableWrapper}>
            <div className={Styles.tableBody}>
              <div className={Styles.warpper}>
                <OrderListSchedule title="Pendentes">
                  {pendingOrders.map((order: KitchenOrder) => {
                    const initial =
                      getClientNameForOrder(order)[0].toUpperCase();

                    return (
                      <div key={order.id}>
                        {!alphabetRefs.current[initial] && (
                          <div
                            ref={(el) => {
                              alphabetRefs.current[initial] = el;
                            }}
                          />
                        )}
                        <CardOrderSchedule
                          {...order}
                          name={getClientNameForOrder(order)}
                          key={order.id}
                          enrollment={order?.client?.identityNumber || ""}
                          orderAction={handleProgressOrder}
                          cancelOrder={() => handleCancelOrder(order)}
                          selectOrder={() => handleOpenModal(order)}
                          handleProfileClick={() =>
                            setClientProfile(order.client)}
                          isLoading={isLoading || isloadingProgress || isCanceling}
                          onPrint={(id: string) => handlePrintOrder(id)}
                          hasPrinter={printers.length > 0}
                        />
                      </div>
                    );
                  })}
                </OrderListSchedule>
                <OrderListSchedule title="Em Andamento">
                  {progressOrders.map((order: KitchenOrder) => (
                    <div key={order.id}>
                      <CardOrderSchedule
                        {...order}
                        name={getClientNameForOrder(order)}
                        key={order.id}
                        enrollment={order?.client?.identityNumber || ""}
                        orderAction={handleDeliverOrder}
                        cancelOrder={() => handleCancelOrder(order)}
                        selectOrder={() => handleOpenModal(order)}
                        handleProfileClick={() =>
                          setClientProfile(order.client)}
                        isLoading={isLoading || isloadingDelivery || isCanceling}
                        onPrint={(id: string) => handlePrintOrder(id)}
                        hasPrinter={printers.length > 0}
                      />
                    </div>
                  ))}
                </OrderListSchedule>
              </div>
            </div>
            <div className={Styles.alphabetIndex}>
              <AlphabetIndex
                letters={alphabet}
                availableLetters={availableLetters}
                onSelect={handleSelectLetter}
              />
            </div>
          </div>
        )}
      </div>

      {selectedOrder && (
        <EditRequest
          order={selectedOrder}
          open={openModal}
          onClose={handleCloseModal}
          disableProductsActionButtons
        />
      )}

      {clientProfile && (
        <ProfileModal
          open={!!clientProfile}
          onClose={() => setClientProfile(null)}
          {...clientProfile}
        />
      )}
    </Container>
  );
};
